// ═══ Wavesurfer Timeline — Zoomable, annotatable waveform editor ═══
//
// Replaces the basic canvas waveform with wavesurfer.js.
// Features: zoom, beat markers, section boundaries, draggable lyric blocks.

import { useRef, useEffect, useCallback, useState } from 'react'
import WaveSurfer from 'wavesurfer.js'
import type { LyricBlock, Section } from './types'

// Re-export for convenience
export type { BeatAnalysis } from './BeatDetection'

interface WavesurferTimelineProps {
  audioUrl: string | undefined
  durationMs: number
  zoom: number                // px per second
  playheadMs: number
  isPlaying: boolean
  sections: Section[]
  lyrics: LyricBlock[]
  beatAnalysis: { bpm: number; beats: number[]; confidence: number }
  selectedBlockId: string | null
  onSeek: (ms: number) => void
  onBlockSelect: (id: string | null) => void
  onBlockDrag: (id: string, startMs: number, endMs: number) => void
  height?: number
}

export default function WavesurferTimeline({
  audioUrl,
  durationMs,
  zoom,
  playheadMs,
  isPlaying,
  sections,
  lyrics,
  beatAnalysis,
  selectedBlockId,
  onSeek,
  onBlockSelect,
  onBlockDrag,
  height = 128,
}: WavesurferTimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const wavesurferRef = useRef<WaveSurfer | null>(null)
  const [ready, setReady] = useState(false)
  const [dragging, setDragging] = useState<{
    blockId: string
    edge: 'left' | 'right' | 'move'
    startX: number
    originalStartMs: number
    originalEndMs: number
  } | null>(null)

  // ═══ Initialize WaveSurfer ═══

  useEffect(() => {
    if (!containerRef.current || !audioUrl) return

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: 'rgba(100, 160, 255, 0.4)',
      progressColor: 'rgba(100, 160, 255, 0.7)',
      cursorColor: '#ff4444',
      cursorWidth: 2,
      height,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      normalize: true,
      interact: true,
      hideScrollbar: true,
      minPxPerSec: zoom,
    })

    ws.load(audioUrl)

    ws.on('ready', () => {
      setReady(true)
    })

    ws.on('click', (relativeX: number) => {
      const seekMs = relativeX * durationMs
      onSeek(seekMs)
    })

    wavesurferRef.current = ws

    return () => {
      ws.destroy()
      wavesurferRef.current = null
      setReady(false)
    }
  }, [audioUrl]) // Only recreate on audio change

  // ═══ Update zoom ═══

  useEffect(() => {
    if (wavesurferRef.current && ready) {
      wavesurferRef.current.zoom(zoom)
    }
  }, [zoom, ready])

  // ═══ Sync playhead ═══

  useEffect(() => {
    if (wavesurferRef.current && ready && durationMs > 0) {
      const progress = playheadMs / durationMs
      if (!isPlaying) {
        wavesurferRef.current.seekTo(Math.min(1, Math.max(0, progress)))
      }
    }
  }, [playheadMs, ready, durationMs, isPlaying])

  // ═══ Helper: ms → px ═══

  const msToX = useCallback((ms: number) => (ms / 1000) * zoom, [zoom])
  const xToMs = useCallback((x: number) => (x / zoom) * 1000, [zoom])

  // ═══ Block drag handlers ═══

  const handleBlockMouseDown = useCallback((e: React.MouseEvent, blockId: string, edge: 'left' | 'right' | 'move') => {
    e.stopPropagation()
    const block = lyrics.find(b => b.id === blockId)
    if (!block) return

    setDragging({
      blockId,
      edge,
      startX: e.clientX,
      originalStartMs: block.startMs,
      originalEndMs: block.endMs,
    })
    onBlockSelect(blockId)
  }, [lyrics, onBlockSelect])

  useEffect(() => {
    if (!dragging) return

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - dragging.startX
      const dMs = xToMs(dx)

      let newStart = dragging.originalStartMs
      let newEnd = dragging.originalEndMs

      if (dragging.edge === 'move') {
        newStart = Math.max(0, dragging.originalStartMs + dMs)
        newEnd = newStart + (dragging.originalEndMs - dragging.originalStartMs)
      } else if (dragging.edge === 'left') {
        newStart = Math.max(0, Math.min(dragging.originalEndMs - 100, dragging.originalStartMs + dMs))
      } else {
        newEnd = Math.max(dragging.originalStartMs + 100, dragging.originalEndMs + dMs)
      }

      onBlockDrag(dragging.blockId, Math.round(newStart), Math.round(newEnd))
    }

    const handleMouseUp = () => setDragging(null)

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [dragging, xToMs, onBlockDrag])

  // ═══ Render ═══

  const totalWidth = durationMs > 0 ? msToX(durationMs) : 0

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Wavesurfer waveform */}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height,
          background: 'rgba(0,0,0,0.2)',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      />

      {/* Overlay: beat markers */}
      {ready && beatAnalysis.beats.length > 0 && (
        <div style={{
          position: 'absolute', top: 0, left: 0,
          width: totalWidth, height,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}>
          {beatAnalysis.beats.map((beatMs, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: msToX(beatMs),
                top: 0,
                width: 1,
                height: '100%',
                backgroundColor: 'rgba(255, 100, 100, 0.3)',
              }}
            />
          ))}
        </div>
      )}

      {/* Overlay: section boundaries */}
      {ready && sections.length > 0 && (
        <div style={{
          position: 'absolute', top: 0, left: 0,
          width: totalWidth, height,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}>
          {sections.map(section => (
            <div
              key={section.id}
              style={{
                position: 'absolute',
                left: msToX(section.startMs),
                width: Math.max(2, msToX(section.endMs - section.startMs)),
                top: 0,
                height: 20,
                backgroundColor: section.backgroundColor,
                opacity: 0.5,
                borderBottom: `2px solid ${section.accentColor || section.backgroundColor}`,
                fontSize: 9,
                color: '#fff',
                padding: '2px 4px',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {section.name}
            </div>
          ))}
        </div>
      )}

      {/* Overlay: lyric blocks (draggable) */}
      {ready && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0,
          width: totalWidth, height: 28,
          overflow: 'hidden',
        }}>
          {lyrics.map(block => {
            const isSelected = block.id === selectedBlockId
            const left = msToX(block.startMs)
            const width = Math.max(4, msToX(block.endMs - block.startMs))

            return (
              <div
                key={block.id}
                style={{
                  position: 'absolute',
                  left,
                  width,
                  height: 24,
                  top: 2,
                  background: isSelected ? 'rgba(255, 200, 50, 0.6)' : 'rgba(255, 255, 255, 0.15)',
                  border: isSelected ? '1px solid #ffc832' : '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 3,
                  fontSize: 9,
                  color: '#fff',
                  padding: '0 4px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'grab',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  userSelect: 'none',
                }}
                onMouseDown={(e) => handleBlockMouseDown(e, block.id, 'move')}
              >
                {/* Left resize handle */}
                <div
                  style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: 6,
                    cursor: 'ew-resize',
                  }}
                  onMouseDown={(e) => handleBlockMouseDown(e, block.id, 'left')}
                />
                <span style={{ pointerEvents: 'none' }}>{block.text.slice(0, 30)}</span>
                {/* Right resize handle */}
                <div
                  style={{
                    position: 'absolute', right: 0, top: 0, bottom: 0, width: 6,
                    cursor: 'ew-resize',
                  }}
                  onMouseDown={(e) => handleBlockMouseDown(e, block.id, 'right')}
                />
              </div>
            )
          })}
        </div>
      )}

      {/* BPM badge */}
      {beatAnalysis.bpm > 0 && (
        <div style={{
          position: 'absolute', top: 4, right: 8,
          background: 'rgba(0,0,0,0.6)',
          color: '#ff6b6b',
          fontSize: 11,
          fontWeight: 600,
          padding: '2px 8px',
          borderRadius: 10,
          pointerEvents: 'none',
        }}>
          {beatAnalysis.bpm} BPM
        </div>
      )}

      {/* Loading state */}
      {!ready && audioUrl && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-secondary)', fontSize: 12,
        }}>
          Loading waveform...
        </div>
      )}
    </div>
  )
}
